import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { functions } from '@/shared/lib/supabase';
import { logger } from '@/shared/lib/logger';

interface CredentialRequirement {
  id: string;
  name: string;
  type: string;
  displayName: string;
  isConfigured: boolean;
  fields?: {
    name: string;
    displayName: string;
    type: string;
    required: boolean;
  }[];
}

interface WorkflowCredentialsResponse {
  credentials: CredentialRequirement[];
  workflow_id: string;
  workflow_name: string;
}

interface CreateCredentialParams {
  client_product_id: string;
  credential_type: string;
  credential_data: Record<string, any>;
}

interface CreateCredentialResponse {
  success: boolean;
  credential_id: string;
  credential_name: string;
  message: string;
}

interface DeleteCredentialParams {
  client_product_id: string;
  credential_type: string;
}

interface DeleteCredentialResponse {
  success: boolean;
  message: string;
}

export const useWorkflowCredentials = (clientProductId: string | null) => {
  return useQuery<WorkflowCredentialsResponse>({
    queryKey: ['workflow-credentials', clientProductId],
    queryFn: async () => {
      if (!clientProductId) {
        return { credentials: [], workflow_id: '', workflow_name: '' };
      }
      return await functions.getWorkflowCredentials(clientProductId);
    },
    enabled: !!clientProductId,
    retry: false,
    staleTime: 60000, // 1 minute
  });
};

export const useCreateOAuthCredential = () => {
  const queryClient = useQueryClient();

  return useMutation<CreateCredentialResponse, Error, CreateCredentialParams>({
    mutationFn: async (params) => {
      return await functions.createOAuthCredential(params);
    },
    onSuccess: (_, variables) => {
      // Invalidate credentials query to refresh the list
      queryClient.invalidateQueries({ 
        queryKey: ['workflow-credentials', variables.client_product_id] 
      });
      queryClient.invalidateQueries({ queryKey: ['client-products', variables.client_product_id] });
    },
    onError: (error: any) => {
      logger.error('OAuth Credential Error', error, {
        message: error?.message,
        name: error?.name,
        status: error?.context?.status,
        error: error?.context?.error,
        details: error?.context?.message || error?.context?.detail,
      });
    },
  });
};

export const useDeleteOAuthCredential = () => {
  const queryClient = useQueryClient();

  return useMutation<DeleteCredentialResponse, Error, DeleteCredentialParams>({
    mutationFn: async (params) => {
      return await functions.deleteOAuthCredential(params);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['workflow-credentials', variables.client_product_id] 
      });
    },
  });
};

interface TelegramWebhookInfo {
  webhookUrl: string;
  webhookId: string;
  hasCredential: boolean;
  credentialName?: string;
  credentialId?: string;
  hasTelegramNode: boolean;
  totalTelegramNodes: number;
}

interface CreateTelegramCredentialParams {
  client_product_id: string;
  access_token: string;
}

interface CreateTelegramCredentialResponse {
  success: boolean;
  credential_id: string;
  message: string;
}

export const useTelegramWebhookInfo = (clientProductId: string | null) => {
  return useQuery<TelegramWebhookInfo>({
    queryKey: ['telegram-webhook-info', clientProductId],
    queryFn: async () => {
      if (!clientProductId) {
        throw new Error('Client product ID is required');
      }
      return await functions.getTelegramWebhookInfo({ client_product_id: clientProductId });
    },
    enabled: !!clientProductId,
    retry: false,
    staleTime: 60000, // 1 minute
  });
};

export const useCreateTelegramCredential = () => {
  const queryClient = useQueryClient();

  return useMutation<CreateTelegramCredentialResponse, Error, CreateTelegramCredentialParams>({
    mutationFn: async (params) => {
      return await functions.createTelegramCredential(params);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['telegram-webhook-info', variables.client_product_id] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['workflow-credentials', variables.client_product_id] 
      });
      queryClient.invalidateQueries({ queryKey: ['client-products', variables.client_product_id] });
    },
  });
};
